/* lp-cart-ladder — the cart drawer's repaint, pair card and upsell rail.

   THE MOST DANGEROUS FILE IN THIS PROJECT. Every other lp- script paints
   pixels or adds to a cart. This one DELETES lines from a live shopper's
   cart, reacting to an event that its own mutations re-fire. It inherits,
   verbatim, the rules the deleted lp-bundle-cart-guard.js paid for in
   production. They are not re-derived here and they are not negotiable:

     1. ONLY OUR LINES. A line is in scope only if it carries _cn_gift. A
        shopper's own purchased fidget is untouchable even when it is the
        exact same variant as a gift we once owed them.
     2. ONLY BY LINE KEY. Never by variant id. Two lines can share a variant.
     3. NEVER TOUCH A NON-GIFT LINE. Removing _cn_gift lines is this file's
        entire cart vocabulary — there is no code path here that can remove
        something a shopper chose.
     4. WHEN ANYTHING IS AMBIGUOUS, DO NOTHING. An unkeyed line, a dropped
        request: all of them leave the cart alone. A false positive destroys a
        cart somebody built on purpose.

   WHAT CHANGED ON 2026-08-20, and why rule 5 ("add before remove") is gone:
   this file no longer grants gifts, so there is nothing to add and no
   threshold to protect. Kaching gives the gifts — see the block above
   planGiftRevocation() for the collision that forced the split, and
   docs/superpowers/specs/2026-08-16-cart-drawer-design.md §6.7 for the
   decision it implements. The ladder is now a DISPLAY surface: the milestone
   track, its labels and the "you're saving" figures are all rendered in
   sections/cart-drawer.liquid from the Gift offer theme settings, and this
   file only takes back the priced lines the old granting path left behind.

   THE THREE THINGS IT STILL DOES:
     - REPAINT the drawer when the cart it was rendered against has moved on,
       which is the race an app's second request always creates;
     - wire the PAIR CARD, whose activate button hands off to
       assets/lp-kaching-bundle.js — a bare add there was the £150 bug;
     - wire the UPSELL RAIL's plain adds.

   THE PLANNER IS PURE ON PURPOSE. planGiftRevocation() takes cart lines and
   returns intent — it does no I/O and touches no DOM. That is what lets
   scripts/cart-ladder-plan.test.mjs interrogate rules 1 to 4 in a millisecond
   instead of only against a real store. If a change here ever makes those
   tests need a real document, logic has leaked out of the pure layer — move
   it back rather than growing the stub. */
(() => {
  'use strict';

  const GIFT_PROP = '_cn_gift';
  const GIFT_STAMP = '1';

  /* ---------------------------------------------------------------- pure */

  const props = (line) => (line && line.properties) || {};

  const isGiftLine = (line) => Object.prototype.hasOwnProperty.call(props(line), GIFT_PROP);

  /* THE LADDER NO LONGER GRANTS GIFTS — IT ONLY TAKES BACK ITS OWN.

     Kaching is configured to give the fidgets, the stress balls, the cap and
     the £299 community access itself, as ordinary lines discounted to £0 by
     its own Function. This ladder was configured with the SAME products, but
     pointed at their real, priced variants, and nothing in a theme can set a
     line price. So every gift it granted was a duplicate of one Kaching had
     already given, charged at £5, £6 or £19, and rendered "FREE" by
     snippets/lp-cart-line.liquid because that snippet trusts the _cn_gift
     stamp. A shopper would have been billed for a gift the drawer promised.

     docs/superpowers/specs/2026-08-16-cart-drawer-design.md §6.7 named this
     exact collision as the one way the two systems could conflict and asked
     for Kaching's gift configuration to be verified before shipping. It was
     verified on 2026-08-20: Kaching does give gifts. Per that section, one
     side had to give way, and the merchandising decision was that Kaching
     keeps the gifts and this ladder becomes a DISPLAY surface — the milestone
     track and its copy still render from theme settings in
     sections/cart-drawer.liquid, and nothing here adds a line.

     What remains is a one-way revocation, because carts outlive a deploy: a
     cart built before this change can still be carrying a charged _cn_gift
     line labelled FREE, and leaving it there overcharges somebody. Rules 1
     and 2 still hold and matter more than ever — only lines carrying
     _cn_gift, only by line key. A shopper's own purchased fidget is
     untouchable even at the exact same variant. */
  const planGiftRevocation = (lines) => {
    if (!Array.isArray(lines)) return { removes: [] };
    const removes = [];
    for (const line of lines) {
      if (!line || !isGiftLine(line)) continue; // RULE 1
      if (typeof line.key !== 'string' || !line.key) continue; // RULE 4 — unkeyed, so unremovable
      /* Absolute target quantity, not a delta — cart/change.js takes a
         quantity and 0 deletes the line. */
      removes.push({ key: line.key, quantity: 0 }); // RULE 2
    }
    return { removes };
  };

  window.lpCartLadder = {
    planGiftRevocation,
    GIFT_PROP,
    GIFT_STAMP,
  };

  /* ------------------------------------------------------------- runtime
     Everything below touches the network and the DOM. Nothing below is
     imported by the tests — if a decision worth testing appears here, move
     it up into the pure layer rather than reaching for a browser harness. */

  /* The vm harness in scripts/cart-ladder-plan.test.mjs evaluates this file
     against a stub global, so bail before wiring anything — the planner
     above is already exported by then, which is all the tests want.

     The test is `window.location`, not the presence of `document`: the stub
     has to provide a document (the IIFE would throw on the first reference
     otherwise), so testing for one proves nothing, and a stub that happens
     to satisfy the guard silently arms real timers and a real fetch inside
     the test run. Nothing but a browser has a location. */
  if (typeof window === 'undefined' || !window.location) return;

  const routes = () => (window.theme && window.theme.routes) || {};
  const cartUrl = () => `${routes().root_url || '/'}cart.js`.replace('//cart.js', '/cart.js');

  const postJSON = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
      return data;
    });

  /* -------------------------------------------------------- reconciliation */

  let inFlight = false;
  let queued = null;
  /* Counter, not a boolean: the reconciler issues an add and then one change
     per removal, so overlapping writes would clear a flag early and let the
     observer treat our own tail requests as somebody else's. */
  let ourWrites = 0;

  const reconcile = async () => {
    /* Guard one of two: our own writes fire cartUpdate, which lands back
       here. Without the lock the drawer would ping-pong until the shopper
       closed the tab. */
    if (inFlight) return;

    inFlight = true;
    try {
      const cart = await fetch(cartUrl(), { headers: { Accept: 'application/json' } }).then((r) => r.json());

      /* THE DRAWER CAN BE STALER THAN THE CART, so check that before
         anything else. The theme re-renders the drawer from the section as
         soon as its own add resolves — but a bundling app (Kaching) adds its
         lines on a separate request that finishes later, so the shopper is
         left looking at a drawer rendered against a cart that no longer
         exists. In the worst case it renders the EMPTY state over a cart with
         six lines in it. Comparing the count the section was rendered with
         against the live cart catches every version of that race, whoever
         caused it, without this file knowing anything about the app.

         This is now the reconciler's MAIN job. Since the ladder stopped
         granting gifts there is nothing to settle on a normal cart, and this
         repaint is the whole reason the pass still runs. */
      const host = document.querySelector('[data-cart-count]');
      const rendered = host ? Number(host.getAttribute('data-cart-count')) : null;
      if (rendered !== null && Number.isFinite(rendered) && rendered !== cart.item_count) {
        repaint(false);
        return;
      }

      const plan = planGiftRevocation(cart.items || []);
      /* Guard two of two, and the one that guarantees termination: a cart with
         no _cn_gift line plans nothing, which is every cart once the last
         pre-change cart has drained. */
      if (!plan.removes.length) return;

      ourWrites += 1;
      for (const removal of plan.removes) {
        /* RULE 2 — by line key, never by variant id. */
        await postJSON(`${routes().cart_change_url || '/cart/change'}.js`.replace('.js.js', '.js'), {
          id: removal.key,
          quantity: removal.quantity,
        });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } catch (err) {
      /* Rule 4 at the outer edge: a dropped request leaves the cart alone and
         the next pass tries again. Revocation is idempotent, so a partial run
         is only ever a shorter run. */
      console.warn('[lp-cart-ladder] reconcile aborted', err);
    } finally {
      inFlight = false;
      if (ourWrites > 0) ourWrites -= 1;
      if (queued) {
        const run = queued;
        queued = null;
        run();
      }
    }
  };

  /* A shopper drumming the quantity stepper should produce one reconcile,
     not eight. */
  let debounceTimer = null;
  const scheduleReconcile = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      if (inFlight) queued = reconcile;
      else reconcile();
    }, 250);
  };

  /* ------------------------------------------------------------ pair card */

  const setupPairCard = (card) => {
    if (card.dataset.lpCartPairReady === 'true') return;
    card.dataset.lpCartPairReady = 'true';

    const read = (selector) => {
      const el = card.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch {
        return null;
      }
    };

    const variants = read('[data-lp-cart-variants]') || [];
    const matched = read('[data-lp-cart-matched]') || {};
    const groups = Array.from(card.querySelectorAll('[data-lp-cart-opt]'));
    const button = card.querySelector('[data-lp-cart-activate]');
    const cue = card.querySelector('[data-lp-cart-pair-cue]');
    const errorEl = card.querySelector('[data-lp-cart-pair-error]');
    if (!button || !groups.length) return;

    const baseCue = cue ? cue.textContent.trim() : '';

    /* THE ONE-TAP CONTRACT. Every axis except colour is pre-selected from
       the hoodie already in the cart and drawn dashed — suggested, not
       confirmed. Colour is deliberately left empty, because a DIFFERENT
       colour is the justification the second unit needs, and defaulting it
       throws that away. So exactly one tap completes the selection. */
    groups.forEach((group) => {
      if (group.hasAttribute('data-colour-axis')) return;
      const name = group.getAttribute('data-option-name');
      const want = matched[name];
      if (!want) return;
      const input = Array.from(group.querySelectorAll('[data-lp-cart-choice]')).find((i) => i.value === want);
      if (!input) return;
      input.checked = true;
      const label = input.nextElementSibling;
      if (label) label.classList.add('is-prematched');
      const hint = group.querySelector('[data-lp-cart-opt-hint]');
      if (hint) hint.textContent = `matched to your first — ${want}`;
    });

    const selection = () =>
      groups.map((group) => {
        const checked = group.querySelector('[data-lp-cart-choice]:checked');
        return checked ? checked.value : null;
      });

    const resolve = () => {
      const chosen = selection();
      if (chosen.some((v) => v === null)) return null;
      return variants.find((v) => chosen.every((value, i) => v.options[i] === value)) || null;
    };

    const sync = () => {
      const chosen = selection();
      const variant = resolve();

      const colourGroup = groups.find((g) => g.hasAttribute('data-colour-axis'));
      if (colourGroup) {
        const picked = colourGroup.querySelector('[data-lp-cart-choice]:checked');
        const valueEl = colourGroup.querySelector('[data-lp-cart-opt-value]');
        if (valueEl) valueEl.textContent = picked ? picked.value : '';

        /* The micro-copy that carries the whole interaction: choosing a
           colour turns the prompt into an acknowledgement plus the one
           remaining instruction. Without it, a pre-selected size looks like
           nothing happened. */
        if (cue) {
          if (picked) {
            const others = groups.filter((g) => g !== colourGroup);
            const remaining = others
              .map((g) => (g.getAttribute('data-option-name') || '').toLowerCase())
              .join(' and ');
            cue.textContent = remaining
              ? `${picked.value} — great choice! Now just confirm your ${remaining}.`
              : `${picked.value} — great choice!`;
            cue.classList.add('is-cued');
          } else {
            cue.textContent = baseCue;
            cue.classList.remove('is-cued');
          }
        }

        /* Once a colour is chosen the pre-matched size stops being a
           suggestion and becomes the selection, so it loses the dashed
           ring. It stays fully changeable. */
        if (picked) {
          groups.forEach((g) => {
            g.querySelectorAll('.is-prematched').forEach((el) => el.classList.remove('is-prematched'));
          });
        }
      }

      if (errorEl) errorEl.hidden = true;

      if (variant && variant.available) {
        button.disabled = false;
        button.dataset.variantId = String(variant.id);
        button.textContent = button.dataset.liveLabel || 'Add';
      } else {
        button.disabled = true;
        delete button.dataset.variantId;
        if (chosen.some((v) => v === null)) {
          button.textContent = button.dataset.idleLabel || 'Choose your options';
        } else {
          /* Resolved to nothing, or resolved to something sold out. Say so
             rather than leaving a dead button with no explanation. */
          button.textContent = 'Sold out in that combination';
        }
      }
    };

    card.addEventListener('change', (event) => {
      if (event.target.matches('[data-lp-cart-choice]')) sync();
    });

    /* THIS IS NOT AN ADD, AND THAT IS THE WHOLE POINT.

       It used to be: one /cart/add.js of the chosen variant. That produced a
       cart holding two hoodies at full price and none of the pair's gifts,
       because Kaching prices bundles from a `__kaching_bundles` line property
       and not from what the cart contains. A bare line is invisible to its
       discount Function, and the hoodie already in the cart stays stamped for
       the ONE-hoodie bar, so the button labelled "Activate discount · £52.50"
       settled at £150.00 with no tangle. Reproduced live on 2026-08-20.

       Activating therefore has to REBUILD the bundle for the higher bar, which
       is assets/lp-kaching-bundle.js's job. It verifies the result and puts the
       cart back if Kaching did not price it, so the two failure modes here are
       "it worked" and "nothing changed" — never "silently overcharged". */
    button.addEventListener('click', async () => {
      const id = button.dataset.variantId;
      if (!id || button.disabled) return;
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      const fail = (message) => {
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
        button.disabled = false;
      };
      try {
        const bundler = window.lpKachingBundle;
        if (!bundler || typeof bundler.upgrade !== 'function') {
          /* Refusing beats falling back to the bare add: the bare add is the
             bug, and a wrong price is worse than an unavailable offer. */
          fail('We could not apply that discount just now. Please try again.');
          return;
        }
        const productId = Number(card.dataset.productId);
        const result = await bundler.upgrade({ productId, addVariantId: Number(id), extraUnits: 1 });
        if (!result || !result.ok) {
          fail(
            result && result.reason === 'no-bar'
              ? 'That offer is not available for this cart.'
              : 'We could not apply that discount just now. Please try again.'
          );
          return;
        }
        /* Same single path into the repaint as before — the drawer re-renders
           from the section, so every figure in it comes from the real cart. */
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
      } catch (err) {
        fail((err.data && err.data.description) || 'That one just sold out. Try another colour.');
      } finally {
        button.removeAttribute('aria-busy');
      }
    });

    sync();
  };

  /* ---------------------------------------------------------- upsell adds */

  const onUpsellClick = async (button) => {
    const id = button.dataset.variantId;
    if (!id || button.getAttribute('aria-busy') === 'true') return;
    button.setAttribute('aria-busy', 'true');
    try {
      await postJSON(`${routes().cart_add_url || '/cart/add'}.js`.replace('.js.js', '.js'), {
        items: [{ id: Number(id), quantity: 1 }],
      });
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    } catch (err) {
      console.warn('[lp-cart-ladder] upsell add failed', err);
      button.textContent = 'Sold out';
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  /* ------------------------------------------------- cart-change observer

     WHY THIS EXISTS. The theme opens the drawer from inside its own
     product-form submit handler — `this.cartDrawer?.show()` at the end of
     assets/theme.js's add. That only fires for adds the THEME made. This
     store runs Kaching, which owns the add-to-cart button on product pages
     and posts its own bundle by its own route, so the theme's handler never
     runs: items land in the cart and nothing visible happens.

     WHY IT WATCHES THE DOM AND NOT THE NETWORK. The first version of this
     wrapped window.fetch. That works, but it puts our code in front of every
     request every other script on this page makes — Shopify's own event
     listener, Intelligems, Kaching — and a mistake in it breaks THEIR
     requests, not just ours. It did exactly that once already. Not worth it
     for a cosmetic trigger.

     The theme keeps a <cart-count> in the header and every add updates it,
     whoever made the add. Observing that element is completely passive: it
     cannot fail anyone else's request, it needs no knowledge of any app, and
     the worst case if an app somehow does not update the count is that the
     drawer does not auto-open — the same behaviour as today, never worse.

     We are not changing WHAT gets added. That stays entirely the app's
     business. We only react to the cart having changed, which is this
     drawer's whole remit. */

  let observerTimer = null;
  let settleTimer = null;
  let lastSeenCount = null;

  const readCount = () => {
    const el = document.querySelector('cart-count, [data-cart-count-bubble], .cart-count');
    if (!el) return null;
    const n = parseInt((el.textContent || '').replace(/\D+/g, ''), 10);
    return Number.isFinite(n) ? n : null;
  };

  /* REPAINT OURSELVES RATHER THAN ASK THE THEME TO.

     Dispatching the theme's `cart:refresh` ought to do this — its handler
     re-fetches the section and calls show(). In practice the drawer opened
     and kept showing the empty state it had been rendered with, and chasing
     the theme's event plumbing through three layers of app wrappers was not
     converging. This does the same two things directly, against the section
     id the drawer element already carries, so it is inspectable and cannot
     silently no-op:

       fetch ?section_id=<the real id>  ->  swap #MiniCart-<id>'s innerHTML

     The long, group-prefixed id matters: `?section_id=cart-drawer` renders a
     DIFFERENT, unconfigured section — no blocks, default settings. Always
     read it off data-section-id, never hardcode it. */
  /* THE DRAWER MUST NOT ASSERT A CART IT IS ABOUT TO CONTRADICT.

     Opening instantly means opening before the app's add has landed, so the
     drawer briefly renders the PREVIOUS cart — and the previous cart is the
     one where the shopper had a single hoodie, so it flashed the "make it
     the pair" upsell at somebody who had just bought the pair. Opening fast
     is right; showing stale content for 500ms is not.

     So the open is instant and the CONTENT is honest: mark the drawer busy
     on the click, render a plain "updating" state, and reveal the real cart
     the moment the repaint lands. The flag lives on <cart-drawer>, outside
     #MiniCart, because the repaint replaces that element's innerHTML and
     would take the flag with it. */
  const setBusy = (busy) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    if (busy) drawer.setAttribute('data-lp-cart-busy', '');
    else drawer.removeAttribute('data-lp-cart-busy');
  };

  const repaint = async (open) => {
    const drawer = document.querySelector('cart-drawer');
    const sectionId = drawer && drawer.getAttribute('data-section-id');
    if (!sectionId) return;
    const host = document.getElementById(`MiniCart-${sectionId}`);
    if (!host) return;
    try {
      const root = (routes().root_url || '/').replace(/\/$/, '');
      const url = `${root}/?section_id=${encodeURIComponent(sectionId)}`;
      const text = await fetch(url, { headers: { Accept: 'text/html' } }).then((r) => r.text());
      const next = new DOMParser()
        .parseFromString(text, 'text/html')
        .getElementById(`MiniCart-${sectionId}`);
      if (next) host.innerHTML = next.innerHTML;
      init();
    } catch (err) {
      console.warn('[lp-cart-ladder] repaint failed', err);
    } finally {
      /* Always clears, including on failure: a drawer stuck saying "updating"
         forever is worse than one showing a slightly stale cart. */
      setBusy(false);
    }
    if (open && typeof drawer.show === 'function') drawer.show();
  };

  const onCountChanged = (next) => {
    /* Our own reconcile grants gifts, which moves the count — reacting to it
       would re-open the drawer every time somebody earned a fidget. */
    if (ourWrites > 0) { lastSeenCount = next; return; }
    const grew = lastSeenCount !== null && next > lastSeenCount;
    lastSeenCount = next;

    clearTimeout(observerTimer);
    /* AN APP'S ADD IS SEVERAL REQUESTS, NOT ONE. Kaching posts its bundle and
       then settles the deal, so acting on the first change paints a cart that
       is still being written. Debounce to the last change in a burst, then
       re-check once it has quietened. */
    observerTimer = setTimeout(() => {
      repaint(grew);
      scheduleReconcile();
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => { repaint(false); scheduleReconcile(); }, 1500);
    }, 400);
  };

  /* OBSERVE A STABLE CONTAINER, NOT THE COUNT ELEMENT ITSELF. The theme
     re-renders the header's <cart-count> rather than mutating its text, so an
     observer bound to the element watches a node that has already been thrown
     away — the count went 0 -> 6 and nothing fired. Watching the header
     subtree survives the element being replaced, which is the normal case
     here, and header mutations are rare enough that re-reading a number in
     the callback costs nothing. */
  const countHost = () => {
    const el = document.querySelector('cart-count, [data-cart-count-bubble], .cart-count');
    return (el && (el.closest('header') || el.closest('[class*="header"]'))) || document.body;
  };

  const watchCartCount = () => {
    const host = countHost();
    if (!host || host.dataset.lpCartWatched === 'true') return;
    host.dataset.lpCartWatched = 'true';
    lastSeenCount = readCount();
    new MutationObserver(() => {
      const next = readCount();
      if (next !== null && next !== lastSeenCount) onCountChanged(next);
    }).observe(host, { childList: true, characterData: true, subtree: true });
  };

  /* --------------------------------------------------------- instant open

     OPEN ON THE CLICK, NOT ON THE RESPONSE. Waiting for the cart count to
     change means waiting for the app's round trip — measured at one to two
     seconds, which reads as a dead button. The drawer opens the moment the
     add-to-cart control is pressed, showing the cart as it stands, and the
     count observer fills in the new lines when they land.

     Opening early is safe in a way that adding early would not be: this
     touches nothing, so the worst case for a click that fails (sold out, a
     validation error) is the shopper seeing their existing cart, which is
     never wrong — just not yet updated.

     Delegated from the document and matched on the theme's own submit
     control, so it keeps working when a product form is re-rendered, and it
     covers whatever markup an app puts around the button as long as the
     click passes through one. */
  const ADD_SELECTOR = [
    '[name="add"]',
    '.product-form__submit',
    'button[type="submit"][data-add-to-cart]',
    '[data-lp-cart-add]',
  ].join(',');

  let busyFloorTimer = null;

  const openDrawerNow = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.show !== 'function') return;
    /* Already open — an upsell ADD from inside the drawer. That button shows
       its own busy state; blanking the whole cart under the shopper's finger
       would be worse than the half-second of staleness. */
    if (drawer.hasAttribute('open')) return;

    setBusy(true);
    drawer.show();

    /* A FLOOR, NOT A TIMEOUT. The count observer resolves this as soon as the
       app's add lands. This is the guarantee for everything else: a click
       that adds nothing (sold out, a validation error, the shopper dismissing
       an app modal) still resolves to the real cart quickly instead of
       sitting on "updating" until something happens to change the count. */
    clearTimeout(busyFloorTimer);
    busyFloorTimer = setTimeout(() => repaint(false), 900);
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest && event.target.closest(ADD_SELECTOR);
      if (!trigger || trigger.disabled) return;
      /* The rail's own ADD buttons are handled by onUpsellClick, which already
         runs inside an open drawer — re-showing would fight the animation. */
      if (trigger.hasAttribute('data-lp-cart-add')) return;
      openDrawerNow();
    },
    true /* capture: an app may stop propagation on its own button */
  );

  /* ------------------------------------------------------------- wiring */

  const init = () => {
    const card = document.querySelector('[data-lp-cart-pair]');
    if (card) setupPairCard(card);
    /* Re-run on every drawer re-render: the header count element survives it,
       but a fresh one appears if the header itself is ever re-rendered. */
    watchCartCount();
  };

  document.addEventListener('click', (event) => {
    const add = event.target.closest && event.target.closest('[data-lp-cart-add]');
    if (add) onUpsellClick(add);
  });

  /* The drawer's markup is replaced wholesale on every cart update, so
     initialisation is re-run rather than bound once — and the pair card
     carries its own ready flag so a re-run is idempotent. */
  document.addEventListener('cart:updated', () => {
    init();
    scheduleReconcile();
  });

  /* The theme publishes cartUpdate through its own pubsub for quantity
     changes made inside <cart-items>, which never becomes a DOM event. */
  const subscribeToTheme = () => {
    if (window.theme && window.theme.pubsub && window.theme.pubsub.subscribe) {
      window.theme.pubsub.subscribe('cartUpdate', () => {
        init();
        scheduleReconcile();
      });
      return true;
    }
    return false;
  };
  if (!subscribeToTheme()) {
    /* theme.js is deferred too and may not have run yet. One retry on
       DOMContentLoaded rather than a poll. */
    document.addEventListener('DOMContentLoaded', subscribeToTheme);
  }

  /* Drawer open is a trigger in its own right: the cart can change on the
     cart page, in another tab, or via the checkout back button, none of
     which fire a cartUpdate in this document. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest && event.target.closest('[aria-controls="CartDrawer"]');
    if (opener) scheduleReconcile();
  });

  document.addEventListener('DOMContentLoaded', () => {
    init();
    scheduleReconcile();
  });
  if (document.readyState !== 'loading') {
    init();
    scheduleReconcile();
  }
})();
